import React, { useState, useEffect, useCallback, useRef } from 'react';
import useWebSocket from '../../hooks/useWebSocket';
import { assetsAPI } from '../../services/api';
import { normalizeSymbol } from '@/lib/utils';

function MarketDepth({ symbol }) {
  const { subscribe, unsubscribe } = useWebSocket();
  const [orderbook, setOrderbook] = useState(null);
  const [recentTrades, setRecentTrades] = useState([]);
  const tradesRef = useRef([]);

  // Detect crypto — orderbook streaming is crypto-only
  const isCrypto = symbol && (/\//.test(symbol) || /^[A-Z]{3,5}(USD|USDT|USDC)$/i.test(symbol));

  const handleOrderbook = useCallback((data) => {
    if (!data) return;
    if (normalizeSymbol(data.symbol || '') !== normalizeSymbol(symbol || '')) return;

    setOrderbook((prev) => {
      if (!prev) return data; // first snapshot — use as-is

      // Merge incremental deltas into existing book
      // Alpaca sends partial updates: size=0 means remove the level, otherwise upsert
      const mergeLevels = (existing, incoming) => {
        const map = new Map(existing.map(l => [l.price, l]));
        for (const level of incoming) {
          if (level.size === 0) {
            map.delete(level.price);
          } else {
            map.set(level.price, level);
          }
        }
        return Array.from(map.values());
      };

      return {
        ...prev,
        ...data,
        bids: mergeLevels(prev.bids || [], data.bids || []),
        asks: mergeLevels(prev.asks || [], data.asks || []),
      };
    });
  }, [symbol]);

  const handleTrade = useCallback((data) => {
    if (!data) return;
    if (normalizeSymbol(data.symbol || '') !== normalizeSymbol(symbol || '')) return;
    tradesRef.current = [...tradesRef.current.slice(-49), data];
    setRecentTrades(tradesRef.current.slice(-10).reverse());
  }, [symbol]);

  useEffect(() => {
    if (!isCrypto) return;
    subscribe('ORDERBOOK_UPDATE', handleOrderbook);
    subscribe('TRADE_UPDATE', handleTrade);
    return () => {
      unsubscribe('ORDERBOOK_UPDATE', handleOrderbook);
      unsubscribe('TRADE_UPDATE', handleTrade);
    };
  }, [isCrypto, subscribe, unsubscribe, handleOrderbook, handleTrade]);

  // Reset + fetch cached data on symbol change
  useEffect(() => {
    setOrderbook(null);
    setRecentTrades([]);
    tradesRef.current = [];

    if (!isCrypto || !symbol) return;
    let cancelled = false;
    assetsAPI.orderbook(symbol).then((res) => {
      if (!cancelled && res?.data) setOrderbook(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [symbol, isCrypto]);

  if (!isCrypto) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Market depth is only available for crypto pairs.
      </div>
    );
  }

  const bids = orderbook?.bids || [];
  const asks = orderbook?.asks || [];

  const sortedBids = [...bids].sort((a, b) => b.price - a.price).slice(0, 10);
  const sortedAsks = [...asks].sort((a, b) => a.price - b.price).slice(0, 10);

  const maxSize = Math.max(
    ...sortedBids.map((l) => l.size),
    ...sortedAsks.map((l) => l.size),
    0.0001
  );

  const bestBid = sortedBids[0]?.price || 0;
  const bestAsk = sortedAsks[0]?.price || 0;
  const spread = bestAsk && bestBid ? bestAsk - bestBid : 0;
  const spreadPct = bestAsk && bestBid ? ((spread / bestAsk) * 100).toFixed(4) : '—';
  const midPrice = bestAsk && bestBid ? (bestAsk + bestBid) / 2 : 0;

  const fmt = (n) => {
    if (n >= 1000) return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (n >= 1) return n.toFixed(4);
    return n.toFixed(6);
  };

  const fmtSize = (n) => {
    if (n >= 1) return n.toFixed(4);
    return n.toFixed(6);
  };

  if (!orderbook) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Waiting for orderbook data...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 h-full overflow-y-auto text-xs font-mono">
      {/* Header */}
      <div className="grid grid-cols-3 gap-1 px-2 py-1 text-muted-foreground/60 uppercase tracking-wider text-[10px] font-semibold border-b border-border/30">
        <span>Price</span>
        <span className="text-right">Size</span>
        <span className="text-right">Depth</span>
      </div>

      {/* Asks — reversed so lowest ask is at bottom (closest to spread) */}
      {[...sortedAsks].reverse().map((level, i) => {
        const pct = (level.size / maxSize) * 100;
        return (
          <div key={`ask-${i}`} className="grid grid-cols-3 gap-1 px-2 py-0.5 relative">
            <div
              className="absolute inset-0 bg-red-500/10"
              style={{ width: `${pct}%`, marginLeft: 'auto' }}
            />
            <span className="relative text-red-400">{fmt(level.price)}</span>
            <span className="relative text-right text-foreground/80">{fmtSize(level.size)}</span>
            <span className="relative text-right">
              <span
                className="inline-block h-2.5 bg-red-500/30 rounded-sm"
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
            </span>
          </div>
        );
      })}

      {/* Spread */}
      <div className="flex items-center gap-2 px-2 py-1.5 border-y border-border/40 bg-card/60">
        <span className="text-muted-foreground/60">Spread</span>
        <span className="text-foreground font-semibold">{fmt(spread)}</span>
        <span className="text-muted-foreground/40">({spreadPct}%)</span>
        {midPrice > 0 && (
          <span className="ml-auto text-muted-foreground/60">
            Mid <span className="text-foreground">{fmt(midPrice)}</span>
          </span>
        )}
      </div>

      {/* Bids */}
      {sortedBids.map((level, i) => {
        const pct = (level.size / maxSize) * 100;
        return (
          <div key={`bid-${i}`} className="grid grid-cols-3 gap-1 px-2 py-0.5 relative">
            <div
              className="absolute inset-0 bg-green-500/10"
              style={{ width: `${pct}%` }}
            />
            <span className="relative text-green-400">{fmt(level.price)}</span>
            <span className="relative text-right text-foreground/80">{fmtSize(level.size)}</span>
            <span className="relative text-right">
              <span
                className="inline-block h-2.5 bg-green-500/30 rounded-sm"
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
            </span>
          </div>
        );
      })}

      {/* Recent Trades */}
      {recentTrades.length > 0 && (
        <div className="mt-2 border-t border-border/30 pt-1">
          <div className="px-2 py-1 text-muted-foreground/60 uppercase tracking-wider text-[10px] font-semibold">
            Recent Trades
          </div>
          {recentTrades.map((trade, i) => {
            const isBuy = trade.takerSide === 'B' || trade.takerSide === 'buy';
            return (
              <div key={i} className="grid grid-cols-3 gap-1 px-2 py-0.5">
                <span className={isBuy ? 'text-green-400' : 'text-red-400'}>
                  {fmt(trade.price)}
                </span>
                <span className="text-right text-foreground/80">{fmtSize(trade.size)}</span>
                <span className="text-right text-muted-foreground/50">
                  {new Date(trade.timestamp).toLocaleTimeString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MarketDepth;
